import React, { useMemo } from 'react';

import { useCollection } from '@amzn/awsui-collection-hooks';
import Table from '@amzn/awsui-components-console/table';
import Header from '@amzn/awsui-components-console/header';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import TextFilter from '@amzn/awsui-components-console/text-filter';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Pagination from '@amzn/awsui-components-console/pagination';

import { StringGroupItem } from 'src/generated/graphql';
import { STRING_GROUP_COLUMNS } from 'src/features/namespace/columns';
import { STRING_GROUPS_PAGE_SIZE } from 'src/features/namespace/constants';

interface StringGroupsTableProps {
  /** All loaded items for the current mode (browse or search). */
  displayItems: StringGroupItem[];
  /** True during initial data load. */
  loading: boolean;
  /** Whether a Load More request is currently in flight. */
  isLoadingMore: boolean;
  /** Whether more items are available to load. */
  hasMore: boolean;
  /** Fetch the next batch of items. */
  handleLoadMore: () => void;
  /** Current search text (immediate, not debounced). */
  searchText: string;
  /** Called as the user types; the parent debounces and queries the server. */
  onSearchChange: (text: string) => void;
  /** When true, hide the CX Family ID column (namespace already shows it). */
  hideCxFamilyColumn?: boolean;
  /** Called when the user clicks the Create string group button. */
  onCreateStringGroup: () => void;
}

/** Displays a table of string groups with client-side pagination and sorting,
 * plus a Load More button to fetch additional items from the server. */
const StringGroupsTable: React.FC<StringGroupsTableProps> = ({
  displayItems,
  loading,
  isLoadingMore,
  hasMore,
  handleLoadMore,
  searchText,
  onSearchChange,
  hideCxFamilyColumn,
  onCreateStringGroup,
}) => {
  const columns = useMemo(
    () =>
      hideCxFamilyColumn
        ? STRING_GROUP_COLUMNS.filter((col) => col.id !== 'cxFamilyId')
        : STRING_GROUP_COLUMNS,
    [hideCxFamilyColumn],
  );

  const { items, paginationProps, collectionProps } = useCollection(
    displayItems,
    {
      pagination: { pageSize: STRING_GROUPS_PAGE_SIZE },
      sorting: {
        defaultState: {
          sortingColumn: { sortingField: 'stringGroupId' },
          isDescending: false,
        },
      },
    },
  );

  return (
    <Table<StringGroupItem>
      {...collectionProps}
      variant="container"
      trackBy="stringGroupId"
      stickyHeader={true}
      stickyColumns={{ first: 1 }}
      wrapLines={true}
      loading={loading}
      stripedRows
      resizableColumns
      header={
        <Header variant="awsui-h1-sticky">
          String groups ({displayItems.length})
        </Header>
      }
      filter={
        <TextFilter
          filteringText={searchText}
          filteringPlaceholder="Find string groups"
          onChange={({ detail }) => onSearchChange(detail.filteringText)}
        />
      }
      pagination={
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            variant="primary"
            loading={isLoadingMore}
            disabled={!hasMore || isLoadingMore}
            onClick={handleLoadMore}
          >
            Load more
          </Button>
          <Pagination {...paginationProps} />
        </SpaceBetween>
      }
      columnDefinitions={columns}
      items={items}
      loadingText="Loading string groups"
      skeleton={loading ? { totalRows: 6 } : undefined}
      empty={
        <Box textAlign="center" color="inherit">
          <SpaceBetween size="m">
            <Box variant="strong">No string groups</Box>
            <Box variant="p" color="inherit">
              No string groups found in this namespace. Create a string group to
              get started.
            </Box>
            <Button variant="primary" onClick={onCreateStringGroup}>
              Create string group
            </Button>
          </SpaceBetween>
        </Box>
      }
    />
  );
};

export default StringGroupsTable;
